'use client';

// import { t } from 'i18next';
import { z as zod } from 'zod';
import { env } from 'src/global-config';
import { useLocale } from 'src/locales';

export const createSchemas = () => {
  const { t } = useLocale();
  // 🧩 Base string rules
  const required = zod
    .string()
    .trim()
    .min(1, { message: t('required') });
  const optional = zod.string().optional();

  const optionalNumber = zod
    .string()
    .optional()
    .refine(
      (val) => {
        if (val === undefined || val === '') return true; // allow undefined or empty
        const trimmed = val.trim();
        if (trimmed === '') return false;
        const num = Number(trimmed);
        return !isNaN(num) && num > 0;
      },
      { message: t('invalid') }
    );

  const emailValidation = zod
    .string()
    .min(1, { message: t('required') })
    .email(t('Enter_Valid_EmailId'));

  const mobileNumberValidation = zod
    .string()
    .min(1, { message: t('required') })
    .regex(/^\d+$/, t('mobile_number_must_contain_numbers'))
    .refine(
      (val) => {
        const isSaudi = /^05\d{8}$/.test(val) || /^5\d{8}$/.test(val) || /^9665\d{8}$/.test(val);
        const isIndian = /^91\d{10}$/.test(val);
        return env === 'qa' || env === 'dev' ? isSaudi || isIndian : isSaudi;
      },
      { message: t('invalid_phoneNumber') }
    );

  const OTPValidation = zod
    .string()
    .min(1, { message: t('required') })
    .max(4, { message: t('otp_max_length') })
    .regex(/^\d+$/, t('otp_numbers_only'));

  const numberValidation = zod
    .string()
    .min(1, { message: t('required') })
    .regex(/^\d+$/, t('only_numbers_allowed'));

  const zipCodeValidation = zod.string().optional();

  const dynamicObject = zod.union([
    zod.string().min(1, { message: t('required') }),
    zod
      .object({})
      .passthrough()
      .refine((obj) => Object.keys(obj).length > 0, {
        message: t('required'),
      }),
  ]);

  // ===============================
  // ✅ Individual Schemas
  // ===============================

  const SignInSchema = zod.object({
    mobileNumber: mobileNumberValidation,
  });

  const SignUpSchema = zod.object({
    companyName: required,
    companyEmail: emailValidation,
    vatNumber: required,
    city: required,
    zipCode: required,
    address: required,
    responsiblePersonName: required,
  });

  const OTPSchema = zod.object({
    otp: OTPValidation,
  });

  const ProfileSchema = zod.object({
    companyName: required,
    companyEmail: emailValidation,
    address: optional,
    city: optional,
    zipCode: zipCodeValidation,
    vatNumber: numberValidation,
    responsiblePersonName: required,
  });

  const AddNewUserSchema = zod.object({
    userName: required,
    email: optional,
    designation: optional,
    roleInCompany: dynamicObject,
    storeLocation: optional,
    storeName: optional,
    mobileNumber: mobileNumberValidation,
  });

  const saveLocationSchema = zod.object({
    name: required,
  });

  const WalletAddMoneySchema = zod.object({
    amount: optionalNumber,
  });

  const userRegisterSchema = zod.object({
    name: required,
    email: emailValidation,
  });

  const companyRegisterSchema = zod.object({
    companyName: required,
    companyEmail: emailValidation,
    vatNumber: numberValidation,
    responsiblePersonName: required,
  });

  const serviceFormSchema = zod.object({
    company: required,
    mobile: mobileNumberValidation,
    email: emailValidation,
  });

  const QuotationRequestSchema = zod.object({
    AvgGPS: numberValidation,
    AvgWeight: numberValidation,
    TypeOfLoad: required,
    ExpectedNumberOfShipments: numberValidation,
    OrderFrequency: dynamicObject,
    AdditionalNotes: optional,
    routes: zod.array(
      zod.object({
        truckSize: dynamicObject,
        trucktype: dynamicObject,
        pickupCity: dynamicObject,
        dropOffCity: zod
          .array(
            zod.object({
              label: zod.string(),
              value: zod.string(),
              _id: zod.string().optional(),
              canonicalName: zod.string().optional(),
              variants: zod.array(zod.string()).optional(),
            })
          )
          .min(1, { message: t('required') }),
      })
    ),
  });

  const TravelContactSchema = zod.object({
    name: required,
    mobileNumber: mobileNumberValidation,
    serviceNeeded: dynamicObject,
    email: emailValidation,
  });

  const googleAddressPasteSchema = zod.object({
    address: zod
      .string()
      .min(1, { message: t('required') })
      .superRefine((val, ctx) => {
        const isValidGoogleAddress = (input) => {
          const googleAddressRegex =
            /^(https?:\/\/)?(www\.)?(google\.com|maps\.google\.com|maps\.app\.goo\.gl)\/.*$/;
          return googleAddressRegex.test(input);
        };
        if (!isValidGoogleAddress(val)) {
          ctx.addIssue({
            code: zod.ZodIssueCode.custom,
            message: t('Please_Enter_Google_Maps_URL_only'),
          });
        }
      }),
  });

  const updateContactPersonSchema_ORDER = zod.object({
    pickupLocation: mobileNumberValidation,
    deliveryLocation1: mobileNumberValidation,
    deliveryLocation2: zod
      .string()
      .optional()
      .refine((val) => !val || /^\d{10,12}$/.test(val), {
        message: t('mobile_number_must_be_10_to_12_digits'),
      }),
    note: zod.string().optional(),
  });

  const bookingSchema = zod
    .object({
      pickupLocation_search: dynamicObject,
      deliveryLocation_1_search: dynamicObject,
      deliveryLocation_2_search: dynamicObject.optional().nullable(),
      truckSize: dynamicObject,
      trucktype: dynamicObject,
      quantity: zod.number().min(1, t('quantity_must_be_1')),
      timeAndSlot: dynamicObject,
      isSecondDropLocationVisible: zod.boolean(),
      goodsValue: optionalNumber,
      loadType: dynamicObject,
      loadQauntity: optionalNumber,
      loadTypeText: optional,
    })
    .superRefine((data, ctx) => {
      const isValidObject = (val) =>
        typeof val === 'object' && val !== null && Object.keys(val).length > 0;

      if (data.isSecondDropLocationVisible && !isValidObject(data.deliveryLocation_2_search)) {
        ctx.addIssue({
          path: ['deliveryLocation_2_search'],
          code: zod.ZodIssueCode.custom,
          message: t('required'),
        });
      }

      if (
        isValidObject(data.loadType) &&
        typeof data.loadType.packetPrice === 'number' &&
        data.loadType.packetPrice > 0
      ) {
        if (!data.loadQauntity || Number(data.loadQauntity) <= 0) {
          ctx.addIssue({
            path: ['loadQauntity'],
            code: zod.ZodIssueCode.custom,
            message: t('required'),
          });
        }
      }

      if (
        isValidObject(data.loadType) &&
        data.loadType.label?.toLowerCase() === 'others' &&
        !data.loadTypeText?.trim()
      ) {
        ctx.addIssue({
          path: ['loadTypeText'],
          code: zod.ZodIssueCode.custom,
          message: t('required'),
        });
      }

      const getPlaceId = (val) => (isValidObject(val) ? val.place_id : null);
      const pickupId = getPlaceId(data.pickupLocation_search);
      const delivery1Id = getPlaceId(data.deliveryLocation_1_search);
      const delivery2Id = getPlaceId(data.deliveryLocation_2_search);

      if (pickupId && delivery1Id && pickupId === delivery1Id) {
        ctx.addIssue({
          path: ['deliveryLocation_1_search'],
          code: zod.ZodIssueCode.custom,
          message: t('pickupAndDropLocationCannotBeSame'),
        });
      }

      if (delivery1Id && delivery2Id && delivery1Id === delivery2Id) {
        ctx.addIssue({
          path: ['deliveryLocation_2_search'],
          code: zod.ZodIssueCode.custom,
          message: t('pickupAndDropLocationCannotBeSame'),
        });
      }
    });

  // ===============================
  // ✅ Return all schemas
  // ===============================
  return {
    required,
    optional,
    optionalNumber,
    emailValidation,
    mobileNumberValidation,
    OTPValidation,
    numberValidation,
    zipCodeValidation,
    dynamicObject,
    SignInSchema,
    SignUpSchema,
    OTPSchema,
    ProfileSchema,
    AddNewUserSchema,
    saveLocationSchema,
    WalletAddMoneySchema,
    userRegisterSchema,
    companyRegisterSchema,
    serviceFormSchema,
    TravelContactSchema,
    googleAddressPasteSchema,
    bookingSchema,
    updateContactPersonSchema_ORDER,
    QuotationRequestSchema,
  };
};
